import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';

import { type NavigateFunction, useNavigate, useParams } from 'react-router';

import { PageInformationContext } from '@/context/PageInformationContext';
import { useForwardCatalog, useSpotCatalog } from '@/features/Catalog/api';
import { useDashboard } from '@/features/Dashboard/api';
import { DashboardStep } from '@/features/Dashboard/constants/DashboardStep';
import {
  DASHBOARD_DEFAULT_BASE_NAME,
  TAB_DEFAULT_BASE_NAME
} from '@/features/Dashboard/constants/names';
import { DashboardDrawer } from '@/features/Dashboard/drawer';
import { DrawerStepsProvider } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { AppFormProvider } from '@/features/Dashboard/form/AppForm/context';
import { getTabContent } from '@/features/Dashboard/lib/transformers';
import { EmptyDashboard } from '@/features/Dashboard/pages/Empty';
import { ViewDashboard } from '@/features/Dashboard/pages/View';
import type { DashboardTab, MarketApps } from '@/features/Dashboard/types';
import MarketsSkeleton from '@/pages/Markets/MarketsSkeleton';

import { EditDashboard } from './pages/Edit/EditDashboardPage';
import { getUniqueDashboardName } from './utils/getUniqueDashboardName';

interface InitializeNewDashboardStateParams {
  uniqueDashboardNameForNew: string | null;
  setTitle: (name: string) => void;
  setTabNames: (names: string[]) => void;
  setActiveTab: (index: number) => void;
  setDashboardConfig: (config: MarketApps | undefined) => void;
  setDashboardStructure: (structure: DashboardTab | undefined) => void;
  setStep: (step: DashboardStep) => void;
}

const initializeNewDashboardState = ({
  uniqueDashboardNameForNew,
  setTitle,
  setTabNames,
  setActiveTab,
  setDashboardConfig,
  setDashboardStructure,
  setStep
}: InitializeNewDashboardStateParams) => {
  if (uniqueDashboardNameForNew) {
    setTitle(uniqueDashboardNameForNew);
  }
  setTabNames([TAB_DEFAULT_BASE_NAME]);
  setActiveTab(0);
  setDashboardConfig([]);
  setDashboardStructure(undefined);
  setStep(DashboardStep.View);
};

const navigateToFirstDashboardTab = (
  dashboardData: Record<string, DashboardTab>,
  navigate: NavigateFunction
) => {
  const firstDashboardTitle = Object.keys(dashboardData)[0];
  if (!firstDashboardTitle) return;

  const firstDashboardTabs = dashboardData[firstDashboardTitle];
  const firstTabName = Object.keys(firstDashboardTabs)[0];
  if (!firstTabName) return;

  navigate(
    `/dashboard/${encodeURIComponent(firstDashboardTitle)}/${encodeURIComponent(firstTabName)}`,
    { replace: true }
  );
};

interface ApplyDashboardSelectionParams {
  dashboardName: string;
  tabParam: string | undefined;
  dashboardData: Record<string, DashboardTab>;
  navigate: NavigateFunction;
  setTabNames: (names: string[]) => void;
  setDashboardStructure: (structure: DashboardTab | undefined) => void;
  setActiveTab: (index: number) => void;
  setDashboardConfig: (config: MarketApps | undefined) => void;
}

const applyDashboardSelection = ({
  dashboardName,
  tabParam,
  dashboardData,
  navigate,
  setTabNames,
  setDashboardStructure,
  setActiveTab,
  setDashboardConfig
}: ApplyDashboardSelectionParams) => {
  if (!(dashboardName in dashboardData)) return false;
  const dashboardTabs = dashboardData[dashboardName];

  const names = Object.keys(dashboardTabs);
  setTabNames(names);
  setDashboardStructure(dashboardTabs);

  const decodedTabParam = tabParam ? decodeURIComponent(tabParam) : '';
  if (!decodedTabParam || !names.includes(decodedTabParam)) {
    if (names.length > 0) {
      navigate(`/dashboard/${encodeURIComponent(dashboardName)}/${encodeURIComponent(names[0])}`, {
        replace: true
      });
    }
    return true;
  }

  const tabIndex = names.indexOf(decodedTabParam);
  if (tabIndex >= 0) {
    setActiveTab(tabIndex);
    const tabValue = dashboardTabs[names[tabIndex]];
    setDashboardConfig(getTabContent(tabValue));
  }

  return true;
};

const Dashboard = () => {
  const [title, setTitle] = useState(DASHBOARD_DEFAULT_BASE_NAME);
  const [step, setStep] = useState(DashboardStep.Empty);
  const [dashboardConfig, setDashboardConfig] = useState<MarketApps | undefined>();
  const [dashboardStructure, setDashboardStructure] = useState<DashboardTab | undefined>();
  const [activeTab, setActiveTab] = useState(0);
  const [tabNames, setTabNames] = useState<string[]>([]);
  const { data: dashboardData, isLoading, isFetching } = useDashboard();
  const navigate = useNavigate();
  const previousDashboardIdRef = useRef<string | undefined>(undefined);

  const params = useParams();

  // Generate unique dashboard name for /dashboard/new route
  const uniqueDashboardNameForNew = useMemo(() => {
    if (!dashboardData) return null;
    const existingDashboardNames = Object.keys(dashboardData);
    return getUniqueDashboardName(existingDashboardNames);
  }, [dashboardData]);

  const applyTitle = useEffectEvent((unsanitizedTitle: string) => {
    setTitle(unsanitizedTitle);
  });

  useEffect(() => {
    if (isLoading || isFetching) return;
    if (!dashboardData) return;
    const dashboardParam = params.dashboardId ?? '';

    // Handle /dashboard/new route for creating a new dashboard
    if (dashboardParam === 'new') {
      initializeNewDashboardState({
        uniqueDashboardNameForNew,
        setTitle,
        setTabNames,
        setActiveTab,
        setDashboardConfig,
        setDashboardStructure,
        setStep
      });
      return;
    }

    if (!dashboardParam || dashboardParam.trim() === '') {
      navigateToFirstDashboardTab(dashboardData, navigate);
      return;
    }

    const unsanitizedTitle = decodeURIComponent(dashboardParam);
    applyTitle(unsanitizedTitle);
    const handled = applyDashboardSelection({
      dashboardName: unsanitizedTitle,
      tabParam: params.tabName,
      dashboardData,
      navigate,
      setTabNames,
      setDashboardStructure,
      setActiveTab,
      setDashboardConfig
    });
    if (!handled) {
      navigate('/notfound', { replace: true });
    }
  }, [
    dashboardData,
    navigate,
    params.dashboardId,
    params.tabName,
    uniqueDashboardNameForNew,
    isLoading,
    isFetching
  ]);

  // Use unique dashboard name for /dashboard/new route, otherwise use title
  const effectiveDashboardName = useMemo(
    () =>
      params.dashboardId === 'new' && uniqueDashboardNameForNew ? uniqueDashboardNameForNew : title,
    [params.dashboardId, uniqueDashboardNameForNew, title]
  );

  const handleTabChange = useCallback(
    (tabIndex: number, tabName?: string) => {
      // Use provided tabName or look up from tabNames array
      const newTabName = tabName ?? tabNames[tabIndex];
      if (!newTabName) return;

      // Optimistic update: immediately update activeTab for instant UI response
      setActiveTab(tabIndex);

      // Also optimistically update dashboardConfig if we have the data
      const localTabs = dashboardStructure ?? dashboardData?.[effectiveDashboardName];
      if (localTabs && newTabName in localTabs) {
        setDashboardConfig(getTabContent(localTabs[newTabName]));
      }

      // Update URL in background (non-blocking for UI)
      navigate(
        `/dashboard/${encodeURIComponent(effectiveDashboardName)}/${encodeURIComponent(newTabName)}`
      );
    },
    [navigate, tabNames, effectiveDashboardName, dashboardData, dashboardStructure]
  );

  useForwardCatalog();
  useSpotCatalog();

  // Switch to view mode when dashboard changes while in edit mode
  const switchToViewOnDashboardChange = useEffectEvent(() => {
    setStep(DashboardStep.View);
  });

  useEffect(() => {
    const currentDashboardId = params.dashboardId;
    const previousDashboardId = previousDashboardIdRef.current;

    // If dashboard ID changed and we're in edit mode, switch to view mode
    if (
      step === DashboardStep.Edit &&
      previousDashboardId !== undefined &&
      currentDashboardId !== previousDashboardId
    ) {
      switchToViewOnDashboardChange();
    }

    // Update the ref for next comparison
    previousDashboardIdRef.current = currentDashboardId;
  }, [params.dashboardId, step]);

  const updateDashboardStep = useEffectEvent(() => {
    // Don't override step if we're in Edit mode (creating new dashboard)
    if (step === DashboardStep.Edit) return;
    if (!isLoading && (!dashboardConfig || dashboardConfig.length === 0)) {
      setStep(DashboardStep.Empty);
    } else {
      setStep(DashboardStep.View);
    }
  });

  useEffect(() => {
    updateDashboardStep();
  }, [dashboardConfig, isLoading, step]);
  const content = useMemo(() => {
    if (isLoading) {
      return <MarketsSkeleton />;
    }
    switch (step) {
      case DashboardStep.Empty:
        return (
          <EmptyDashboard
            activeTab={activeTab}
            setStep={setStep}
            tabNames={tabNames}
            onTabChange={handleTabChange}
          />
        );
      case DashboardStep.View:
        return (
          <ViewDashboard
            activeTab={activeTab}
            dashboardConfig={dashboardConfig}
            setStep={setStep}
            tabNames={tabNames}
            title={effectiveDashboardName}
            onTabChange={handleTabChange}
          />
        );
      case DashboardStep.Edit:
        return (
          <EditDashboard
            activeTab={activeTab}
            dashboardConfig={dashboardConfig!}
            dashboardName={effectiveDashboardName}
            dashboardStructure={dashboardStructure}
            initialDashboardName={title}
            onClose={() => {
              setStep(DashboardStep.View);
            }}
          />
        );
      default:
        return null;
    }
  }, [
    isLoading,
    step,
    activeTab,
    tabNames,
    handleTabChange,
    dashboardConfig,
    effectiveDashboardName,
    dashboardStructure,
    title
  ]);

  return (
    <PageInformationContext
      value={{
        mainPageTitle: effectiveDashboardName
      }}
    >
      <AppFormProvider>
        <DrawerStepsProvider>
          {content}
          <DashboardDrawer />
        </DrawerStepsProvider>
      </AppFormProvider>
    </PageInformationContext>
  );
};

export default Dashboard;
